import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import { cn } from '../../utils/cn';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (!email.trim()) {
        setMessage({ type: 'error', text: 'Please enter your email address' });
        return;
      }

      if (!validateEmail(email)) {
        setMessage({ type: 'error', text: 'Please enter a valid email address' });
        return;
      }

      const response = await fetch(API_ENDPOINTS.AUTH_FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim() })
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Password reset instructions have been sent to your email address' 
        });
        setEmail('');
      } else {
        try {
          const errorData = await response.json();
          setMessage({ 
            type: 'error', 
            text: errorData.message || 'Failed to send password reset instructions' 
          });
        } catch {
          setMessage({ 
            type: 'error', 
            text: `Failed to send password reset instructions (${response.status})` 
          });
        }
      }
    } catch (error) {
      console.error('Error sending password reset request:', error);
      setMessage({ 
        type: 'error', 
        text: 'An error occurred while sending password reset instructions' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setMessage(null);
  };

  const inputClass = cn(
    'w-full rounded-xl border-0 bg-gray-100 px-4 py-3 text-slate-900',
    'placeholder:text-slate-400',
    'focus:outline-none focus:ring-2 focus:ring-sky-500/30'
  );

  return (
    <div
      className={cn(
        'min-h-screen homeBg',
        'bg-gradient-to-b from-gray-200 to-gray-900',
        'flex flex-col justify-center',
        'px-4 py-10 sm:px-6 sm:py-12 lg:px-8'
      )}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 lg:flex-row lg:items-stretch lg:justify-center">
        <section
          className={cn(
            'flex flex-1 flex-col rounded-3xl bg-white/95 p-8 shadow-sm backdrop-blur-sm sm:p-10 lg:max-w-xl lg:p-12 xl:p-16 blurLayerInteractiveInteractive'
          )}
        >
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
            Business payment workspace
          </p>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            Make complex payment operations feel obvious from the first click.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-500">
            ZK Gate is built for online businesses that need modern payment flow, clean settlement, and a calmer operational experience for the people running it every day.
          </p>
          <div className="mt-8 rounded-2xl bg-slate-100 p-6 sm:p-8">
            <h2 className="text-sm font-semibold text-slate-900">
              What your team gets right away
            </h2>
            <ul className="mt-4 flex flex-col gap-3">
              {[
                'Create payment links, invoices, and direct crypto collection without exposing the settlement complexity underneath.',
                'Route settlement through the hosted smart-contract service with live visibility into payout progress and exceptions.',
                'Keep operators focused on action, not troubleshooting, with one place for review, retry, and follow-up.',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-600">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500"
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className={cn(
            'flex w-full flex-col rounded-3xl bg-white/95 p-8 shadow-sm backdrop-blur-sm sm:p-10 lg:max-w-md lg:flex-none lg:p-12 xl:p-16'
          )}
        >
          <header>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
              Secure workspace
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">
              Reset access
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Tell us which email owns the account and we will send a secure password-reset link.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            {message && (
              <div
                role={message.type === 'success' ? 'status' : 'alert'}
                className={cn(
                  'rounded-xl border px-4 py-3 text-sm',
                  message.type === 'success'
                    ? 'border-emerald-100 bg-emerald-50 text-emerald-800'
                    : 'border-red-100 bg-red-50 text-red-800'
                )}
              >
                {message.text}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="forgot-email" className="text-sm font-bold text-slate-700">
                Email Address
              </label>
              <input
                id="forgot-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className={inputClass}
              />
              <p className="text-xs text-slate-500">
                We will email instructions to this address. If the account exists, the reset link will arrive shortly.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className={cn(
                'w-full rounded-xl bg-sky-500 py-3.5 text-base font-semibold text-white',
                'shadow-md shadow-sky-500/25',
                'transition hover:bg-sky-600 hover:shadow-sky-500/35',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2',
                'disabled:pointer-events-none disabled:opacity-50',
                'inline-flex items-center justify-center gap-2'
              )}
            >
              {loading && (
                <span
                  className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden
                />
              )}
              <span>{loading ? 'Sending...' : 'Send Reset Instructions'}</span>
            </button>
          </form>

          <footer className="mt-8 flex justify-center">
            <Link
              to="/login"
              className="text-sm font-medium text-sky-600 hover:text-sky-700 focus:outline-none focus-visible:underline"
            >
              Back to login
            </Link>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default ForgotPassword;
